import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../common/Button';

/**
 * MetricCard component for displaying key metrics
 * WCAG 2.1 AA compliant with semantic HTML and ARIA labels
 */

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  /**
   * Plain-English explanation of what this metric means, shown on an (i) icon
   * beside the title on hover and focus.
   *
   * Exists because these tiles are read by people who did not build them. "Waste
   * Index" or "Forecast Accuracy" are not self-evident, and a viewer who has to
   * ask what a number means has already stopped trusting it. Deliberately plain
   * text rather than markup so it can also serve as the `title` attribute, which
   * is what makes it reachable on touch devices and by screen readers.
   */
  info?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'stable';
    label: string;
  };
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  variant = 'default',
  className = '',
  action,
  info,
}) => {
  const variantStyles = {
    default: 'bg-card border-border',
    success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    danger: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800',
  };

  const trendColors = {
    up: 'text-emerald-600 dark:text-emerald-400',
    down: 'text-rose-600 dark:text-rose-400',
    stable: 'text-muted-foreground',
  };

  const trendIcons = {
    up: '↑',
    down: '↓',
    stable: '→',
  };

  return (
    <div
      className={`rounded-lg border p-4 shadow-sm transition-shadow hover:shadow-md ${variantStyles[variant]} ${className}`}
      role="article"
      aria-label={`${title} metric card`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
            {info && (
              /* Rendered as a real <button> rather than a styled span so it is
                 keyboard-reachable and announced; title= carries the same text
                 so it also works on touch, where :hover never fires. */
              <button
                type="button"
                // No `title` attribute on purpose. The browser renders its own
                // native tooltip from title=, which appeared alongside the
                // styled one below - two boxes with identical text for a single
                // hover. aria-label carries the same content for screen readers,
                // and the styled box shows on focus as well as hover, so tapping
                // the icon on a touch device still reveals it.
                aria-label={`What does ${title} mean? ${info}`}
                className="group relative inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-muted-foreground/40 text-[9px] font-semibold leading-none text-muted-foreground transition-colors hover:border-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                i
                <span
                  role="tooltip"
                  // group-focus (not focus-visible): a touch tap focuses the
                  // button but does not always count as "focus-visible", so
                  // focus-visible alone left the tooltip unreachable on mobile.
                  className="pointer-events-none absolute left-1/2 top-5 z-20 hidden w-56 -translate-x-1/2 rounded-md border border-border bg-popover px-2.5 py-2 text-left text-[11px] font-normal leading-snug text-popover-foreground shadow-lg group-hover:block group-focus:block"
                >
                  {info}
                </span>
              </button>
            )}
          </div>
          <p className="mt-1 text-xl font-semibold tracking-tight text-foreground" aria-label={`${title} value`}>
            {value}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
          )}
          {action && (
            <Button onClick={action.onClick} size="sm" className="mt-2" aria-label={action.label}>
              <Plus />
              {action.label}
            </Button>
          )}
          {trend && (
            <div className="mt-1.5 flex items-center gap-1">
              <span
                className={`text-xs font-medium ${trendColors[trend.direction]}`}
                aria-label={`Trend ${trend.direction}`}
              >
                {trendIcons[trend.direction]} {Math.abs(trend.value).toFixed(2)}%
              </span>
              <span className="text-xs text-muted-foreground">{trend.label}</span>
            </div>
          )}
        </div>
        {icon && (
          <div className="ml-3 flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-lg bg-secondary text-muted-foreground dark:bg-secondary dark:text-muted-foreground/70" aria-hidden="true">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};
